import DataGridWrapper, { DataGridToolbar } from '@components/datagrid';
import CustomGridFilterPanel from '@components/datagrid/CustomGridFilterPanel';
import DataGridBox from '@components/datagrid/DataGridBox';
import { valueBetweenOperator } from '@components/datagrid/filterOperators';
import { useMediaQuery, useTheme } from '@mui/material';
import { getGridStringOperators, GridColumns, GridFilterItem, GridFilterModel } from '@mui/x-data-grid-pro';
import { useAppDispatch } from '@src/app/hooks/store';
import { setHoldingFilters } from '@src/app/store/reducers/holdings';
import strings from '@src/app/strings/LocalisedStrings';
import { numberFormatterDollar, numberFormatterHoldings, numberFormatterWithoutSymbols, percentageFormatter } from '@src/app/utils/GenericUtils';
import { holdingsSecurityUrl } from '@src/app/utils/UrlBuilder';
import { Holding, MultiPortfolioHoldingList } from 'clientportalshared';
import { FC, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

export interface HoldingsDataGridProps {
  data: MultiPortfolioHoldingList;
  asOfDate: string;
  filters: GridFilterItem[];
  isLoading: boolean;
  currentPageSize: number;
  setCurrentPageSize: (pageSize: number) => void;
}

const columns: GridColumns = [
  {
    field: 'securityCode',
    headerName: strings.code,
    flex: 1,
    minWidth: 170,
  },
  {
    field: 'securityName',
    headerName: strings.security,
    flex: 1,
    minWidth: 350,
  },
  {
    field: 'assetClassName',
    headerName: strings.assetClassName,
    flex: 1,
    minWidth: 200,
  },
  {
    field: 'units',
    headerName: strings.quantity,
    flex: 1,
    minWidth: 150,
    valueGetter: (params) => numberFormatterWithoutSymbols(params.value || 0, 4),
    valueFormatter: (params) => numberFormatterHoldings(Number(params.value || 0), 4),
    filterOperators: [...getGridStringOperators(), valueBetweenOperator],
  },
  {
    field: 'price',
    headerName: strings.price,
    flex: 1,
    minWidth: 150,
    valueGetter: (params) => numberFormatterWithoutSymbols(params.value || 0, 2),
    valueFormatter: (params) => numberFormatterDollar(Number(params.value || 0), 2),
    filterOperators: [...getGridStringOperators(), valueBetweenOperator],
  },
  {
    field: 'value',
    headerName: strings.marketValue,
    flex: 1,
    minWidth: 150,
    valueGetter: (params) => numberFormatterWithoutSymbols(params.value || 0),
    valueFormatter: (params) => numberFormatterDollar(Number(params.value || 0)),
    filterOperators: [...getGridStringOperators(), valueBetweenOperator],
  },
  {
    field: 'percentageOfPortfolio',
    headerName: strings.weight,
    flex: 1,
    minWidth: 150,
    valueGetter: (params) => numberFormatterWithoutSymbols(params.value || 0),
    valueFormatter: (params) => percentageFormatter(Number(params.value || 0)),
    filterOperators: [...getGridStringOperators(), valueBetweenOperator],
  },
  {
    field: 'costBase',
    headerName: strings.costBase,
    flex: 1,
    minWidth: 150,
    valueGetter: (params) => numberFormatterWithoutSymbols(params.value || 0),
    valueFormatter: (params) => numberFormatterDollar(Number(params.value || 0)),
    filterOperators: [...getGridStringOperators(), valueBetweenOperator],
  },
  {
    field: 'estimatedGainOrLoss',
    headerName: strings.estLossGain,
    minWidth: 150,
    valueGetter: (params) => numberFormatterWithoutSymbols(params.value || 0),
    valueFormatter: (params) => numberFormatterDollar(Number(params.value || 0)),
    filterOperators: [...getGridStringOperators(), valueBetweenOperator],
  },
];

export const HoldingsDataGrid: FC<HoldingsDataGridProps> = ({
  data,
  asOfDate,
  filters,
  isLoading,
  currentPageSize,
  setCurrentPageSize,
}: HoldingsDataGridProps) => {
  const [search, setSearch] = useState<string>('');
  const dispatch = useAppDispatch();
  const history = useHistory();
  const theme = useTheme();
  const small = useMediaQuery(theme.breakpoints.between('xs', 'lg'));

  const rows = useMemo(() => {
    const holdings: Holding[] = data?.holdingList?.data ?? [];
    return holdings.filter(
      (value) =>
        (value.securityCode && value.securityCode.toLowerCase().includes(search.toLowerCase())) ||
        (value.securityName && value.securityName.toLowerCase().includes(search.toLowerCase()))
    );
  }, [search]);

  return (
    <DataGridBox>
      <DataGridWrapper
        loading={isLoading}
        componentsProps={{
          toolbar: {
            csvFileName: `Holdings-${asOfDate}.csv`,
            searchValue: search,
            setSearchValue: setSearch,
          },
        }}
        components={{
          Toolbar: DataGridToolbar,
          FilterPanel: CustomGridFilterPanel,
        }}
        disableSelectionOnClick
        onRowClick={(params) => {
          history.push(holdingsSecurityUrl(params.row['securityCode'], asOfDate));
        }}
        onPageSizeChange={(pageSize: number) => setCurrentPageSize(pageSize)}
        rowsPerPageOptions={small ? [10] : [25, 50, 100]}
        pagination
        pageSize={small ? 10 : currentPageSize}
        localeText={{ noRowsLabel: strings.emptyPortfolioDataMessage }}
        onFilterModelChange={(model: GridFilterModel) => {
          dispatch(setHoldingFilters(model.items));
        }}
        initialState={{
          pinnedColumns: {
            left: ['securityCode'],
          },
          pagination: {
            pageSize: small ? 10 : currentPageSize,
          },
          filter: {
            filterModel: {
              items: filters,
            },
          },
          columns: {
            columnVisibilityModel: {
              securityKey: false,
            },
          },
        }}
        getRowId={(row) => `${row.securityKey}${Math.random()}`}
        columns={columns}
        rows={search.length === 0 ? data?.holdingList?.data ?? [] : rows}
      />
    </DataGridBox>
  );
};
